import {computed, effect, inject, Injectable, Injector, runInInjectionContext, Signal, signal} from '@angular/core';
import {ApiService} from './api.service';
import {catchError, EMPTY, filter, firstValueFrom, map, of, switchMap, tap} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {AUTH_KEYS} from './auth-constants';
import {LoadingService} from './loading.service';
import {IFileDownload} from '../components/file-download/file-download';


export enum UserRole {
  CLIENT = 'CLIENT',
  SELLER = 'SELLER',
  EMPTY_CLIENT = 'EMPTY_CLIENT',
  CLIENT_SELLER = 'CLIENT_SELLER',
  SUPPLIER = 'SUPPLIER',
  OWNER = 'OWNER',
  EMPLOYEE = 'EMPLOYEE'
}

export interface IUser {
  type: string;
  userId: number;
  phone: string;
  firstName: string;
  lastName: string;
  middleName: string;
  creatorUserType: 'OWNER' | 'CLIENT'|'SUPPLIER';
}

export type MenuId =
  | 'SHIPMENTS'
  | 'BRIEFS'
  | 'PRODUCTS'
  | 'WAREHOUSES_SHOWCASE'
  | 'WAREHOUSES'
  | 'STAFF'
  | 'FINANCE'
  | 'COMPANIES'
  | 'WORKS'
  | 'NOMENCLATURE'
  | 'CHATS'
  | 'PRODUCTS_SHOWCASE'
  | 'SELLERS'
  ;

export type IUserType = 'OWNER' | 'CLIENT' | 'EMPLOYEE' |'SUPPLIER' | 'EMPTY_CLIENT' | 'SELLER' | 'CLIENT_SELLER';


export interface IUser {
  id: number;
  email: string;
  role: UserRole;
  is2FaEnabled: boolean;
  profile?: IEmployeeUser | IProfile;
  createdAt: string;
  updatedAt: string;
  active: boolean;
  username: string;
  authorities: string[];

}

export interface IProfile{
  phone:string;
  firstName:string;
  lastName:string;
  middleName:string;
  cityId?:number;
  creatorUserType?:string;
  nominalAccount?:string;
}

export interface IEmployeeUser {
  archived: boolean;
  birthPlace: string;
  birthday: string;
  blocked: boolean;
  carBrand: string;
  carModel: string;
  carNumber: string;
  category: string;
  createdAt: string;
  creatorId: number;
  creatorUserType: string;
  driverLicence: string;
  driverLicenceDate: string;
  driverLicenceDateTo: string;
  firstName: string;
  lastName: string;
  middleName: string;
  lastUpdaterId: number;
  passport: string;
  passportDate: string;
  passportDepartment: string;
  passportValidTo: string;
  phone: string;
  staffRoleId: number;
  telegramId: string;
  type: string;
  updatedAt: string;
  userId: number;
  usesCar: boolean;
  files: IFileDownload[];
}


@Injectable({providedIn: 'root'})
export class AuthService {
  apiService = inject(ApiService);
  loadingService = inject(LoadingService);
  router = inject(Router)
  private userSignal = signal<IUser | null>(null);
  user = computed(() => this.userSignal());
  // isVerified = computed(() => this.user()?.verificationStatus === 'VERIFIED');
  permissions = signal<any>(null);
  clientOrSupplier = computed(()=>this.userTypeCreator() === UserRole.CLIENT  || this.userTypeCreator() === UserRole.SUPPLIER || this.userTypeCreator() === UserRole.CLIENT_SELLER);

  constructor(private injector: Injector) {
  }

  canAccess(
    allowedUserCreateTypes: string[],
    resource: string,
    action: string
  ): Signal<boolean> {

    return computed(() => {
      const staffCan = this.CanByPermissions(resource, action);

      const roleCan = allowedUserCreateTypes.includes(this.user()?.role || '');

      return staffCan || roleCan;
    });

  }

  CanByPermissions(type: string, action: string) {
    const perms = this.permissions();
    return perms?.[type]?.includes(action) ?? false;

  }

  readonly userTypeCreator = computed(() => {
    const user = this.user();

    return user?.role === 'EMPLOYEE'
      ? user?.profile?.creatorUserType ?? ''
      : user?.role;
  });

  readonly isClientUserTypeCreator = computed(() => {

    return this.userTypeCreator() === 'CLIENT'
  });

  readonly isOwnerUserTypeCreator = computed(() => {

    return this.userTypeCreator() === 'OWNER'
  });


  Register(data: any) {
    return this.apiService.SendPost('/user/register', data, this.loadingService.StatesNames.REGISTER).pipe(
      tap((res) => {
        localStorage.setItem(AUTH_KEYS.ACCESS_TOKEN, res[AUTH_KEYS.ACCESS_TOKEN]);
        localStorage.setItem(AUTH_KEYS.REFRESH_TOKEN, res[AUTH_KEYS.REFRESH_TOKEN]);
        this.userSignal.set(res['user']);
      })
    )
  }

  GetPermissionsForStaff() {
    return this.apiService.SendGet('/employee/my-role').pipe(tap((res) => {
      this.permissions.set(res);
      ;

    }));
  }

  HasTokens() {
    return localStorage.getItem(AUTH_KEYS.ACCESS_TOKEN) && localStorage.getItem(AUTH_KEYS.REFRESH_TOKEN)
  }

  RequestPassword(data: any) {
    return this.apiService.SendPost('/user/reset-password/request', data, this.loadingService.StatesNames.REQUEST_PASSWORD).pipe(
      tap((res) => {
      })
    )
  }

  RestorePassword(data: any) {
    return this.apiService.SendPost('/user/reset-password/confirm', data, this.loadingService.StatesNames.RESTORE_PASSWORD).pipe(
      tap((res) => {
      })
    )
  }

  RestorePasswordCheck(data: any) {
    return this.apiService.SendPost('/user/reset-password/check', data, this.loadingService.StatesNames.CHECK_PASSWORD_LINK).pipe(
      tap((res) => {
      })
    )
  }

  ResetPassword(data: any) {
    return this.apiService.SendPost('/auth/self-password-reset', data, this.loadingService.StatesNames.REQUEST_PASSWORD).pipe(
      tap((res) => {
        localStorage.setItem(AUTH_KEYS.ACCESS_TOKEN, res[AUTH_KEYS.ACCESS_TOKEN]);
        localStorage.setItem(AUTH_KEYS.REFRESH_TOKEN, res[AUTH_KEYS.REFRESH_TOKEN]);
        this.userSignal.set(res['user']);
      })
    )
  }

  Login(data: any) {
    return this.apiService.SendPost('/auth', data, this.loadingService.StatesNames.LOGIN).pipe(
      tap((res) => {
        localStorage.setItem(AUTH_KEYS.ACCESS_TOKEN, res[AUTH_KEYS.ACCESS_TOKEN]);
        localStorage.setItem(AUTH_KEYS.REFRESH_TOKEN, res[AUTH_KEYS.REFRESH_TOKEN]);
        this.userSignal.set(res['user']);
      }),
      switchMap(() => {
        if (this.user()?.role === 'EMPLOYEE') {
          return this.GetPermissionsForStaff()
        } else {
          return of(null)
        }

      })
    );
  }

  GetMe() {
    return this.apiService.SendGet('/auth/me', this.loadingService.StatesNames.GET_ME)
      .pipe(
        catchError((err) => {

          if (!window.location.pathname.includes('/auth')) {
            this.router.navigate(['/auth']);
          }
          this.CLearSession();
          return EMPTY;
        }),
        tap((res) => {
            this.userSignal.set(res);
            if (window.location.href.includes('auth')) {
              this.router.navigate(['/base/briefs/list'])
            } else {
              //console.log("window.location.href.includes('auth')",window.location.href.split('base'))
            }
          }
        ),
        switchMap(()=>{
          if(this.user()?.role === 'EMPLOYEE'){
            return this.GetPermissionsForStaff()
          } else {
            return of(null)
          }

        })
      )
  }

  awaitUser(): Promise<any> {
    return new Promise((resolve) => {
      const runner = effect(() => {
        const u = this.userSignal();
        if (u !== null && u !== undefined) {
          resolve(u);
          runner.destroy();
        }
      });
    });
  }


  /*  awaitRole(): Promise<any> {
      return new Promise((resolve) => {
        const runner = effect(() => {
          const u = this.permissions();
          if (u !== null && u !== undefined) {
            resolve(u);
            runner.destroy();
          }
        });
      });
    }*/

  awaitRole(): Promise<any> {
    return new Promise(resolve => {
      runInInjectionContext(this.injector, () => {
        const runner = effect(() => {
          const u = this.permissions();
          if (u != null) {
            resolve(u);
            runner.destroy();
          }
        });
      });
    });
  }

  /*  awaitRole(): Promise<any> {
      return firstValueFrom(
        toObservable(this.permissions).pipe(
          filter(v => v != null)
        )
      );
    }*/

  RefreshToken() {
    return this.apiService.SendPost('/auth/refresh', {}).pipe(tap((res) => {
      localStorage.setItem(AUTH_KEYS.ACCESS_TOKEN, res['token']);
      localStorage.setItem(AUTH_KEYS.REFRESH_TOKEN, res['refresh']);
      this.userSignal.set(res['data']);
    }))
  }

  getCities(){
    return this.apiService.SendGet('/public/cities')
  }


  CLearSession() {
    localStorage.removeItem(AUTH_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(AUTH_KEYS.REFRESH_TOKEN);
    this.userSignal.set({} as IUser);
  }

  logout() {
    return this.apiService.SendPost('/auth/logout', {}).pipe(
      tap(() => {
        this.CLearSession();
        //console.log("CLearSession");
        this.router.navigate(['/auth']);
      })
    )
  }

  updateUser(User: any) {
    const current: any = this.userSignal();
    const updatedUser: any = {
      ...current,
      ...User,
    };

/*    if (updatedUser.profile) {
      updatedUser.profile.creatorUserType = current.profile.creatorUserType

    }
    return user?.role === 'EMPLOYEE'
      ? user?.profile?.creatorUserType ?? ''
      : user?.role;*/


    this.userSignal.set(updatedUser);
    //console.log("user",this.userSignal());
  }
}
